import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { RequirePermissions } from "@/app/router/guards";
import { PermissionCodes } from "@/shared/auth/permissions";
import { LazyPage } from "@/app/router/LazyPage";

const ItemsListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.ItemsListPage })),
);
const ItemDetailPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.ItemDetailPage })),
);
const ItemCategoriesListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.ItemCategoriesListPage })),
);
const UomsListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.UomsListPage })),
);
const WarehousesListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.WarehousesListPage })),
);
const WarehouseDetailPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.WarehouseDetailPage })),
);
const LocationsListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.LocationsListPage })),
);
const LocationDetailPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.LocationDetailPage })),
);
const EquipmentListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.EquipmentListPage })),
);
const EquipmentDetailPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.EquipmentDetailPage })),
);
const EquipmentTypesListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.EquipmentTypesListPage })),
);
const DefectsListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.DefectsListPage })),
);
const ReasonsListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.ReasonsListPage })),
);
const LossTypesListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.LossTypesListPage })),
);
const PartnerSegmentsListPage = lazy(() =>
  import("@/modules/mdm").then((m) => ({ default: m.PartnerSegmentsListPage })),
);

function guard(list: string, manage: string): RouteObject["element"] {
  return <RequirePermissions codes={[list, manage]} match="any" />;
}

export const mdmRoutes: RouteObject[] = [
          {
            element: guard(PermissionCodes.MdmItemRead, PermissionCodes.MdmItemManage),
            children: [
              {
                path: "mdm/items",
                element: (
                  <LazyPage>
                    <ItemsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/items/new",
                element: (
                  <LazyPage>
                    <ItemsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/items/:itemCode/edit",
                element: (
                  <LazyPage>
                    <ItemDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/items/:itemCode",
                element: (
                  <LazyPage>
                    <ItemDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmUomList, PermissionCodes.MdmUomManage),
            children: [
              {
                path: "mdm/item-categories",
                element: (
                  <LazyPage>
                    <ItemCategoriesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/uoms",
                element: (
                  <LazyPage>
                    <UomsListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmWarehouseList, PermissionCodes.MdmWarehouseManage),
            children: [
              {
                path: "mdm/warehouses",
                element: (
                  <LazyPage>
                    <WarehousesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/warehouses/:warehouseCode/edit",
                element: (
                  <LazyPage>
                    <WarehouseDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/warehouses/:warehouseCode",
                element: (
                  <LazyPage>
                    <WarehouseDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmLocationList, PermissionCodes.MdmLocationManage),
            children: [
              {
                path: "mdm/locations",
                element: (
                  <LazyPage>
                    <LocationsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/locations/new",
                element: (
                  <LazyPage>
                    <LocationsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/locations/:locationCode/edit",
                element: (
                  <LazyPage>
                    <LocationDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/locations/:locationCode",
                element: (
                  <LazyPage>
                    <LocationDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmEquipmentList, PermissionCodes.MdmEquipmentManage),
            children: [
              {
                path: "mdm/equipment",
                element: (
                  <LazyPage>
                    <EquipmentListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/equipment/new",
                element: (
                  <LazyPage>
                    <EquipmentListPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/equipment/:equipmentCode/edit",
                element: (
                  <LazyPage>
                    <EquipmentDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/equipment/:equipmentCode",
                element: (
                  <LazyPage>
                    <EquipmentDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "mdm/equipment-types",
                element: (
                  <LazyPage>
                    <EquipmentTypesListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmDefectList, PermissionCodes.MdmDefectManage),
            children: [
              {
                path: "mdm/defects",
                element: (
                  <LazyPage>
                    <DefectsListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmReasonList, PermissionCodes.MdmReasonManage),
            children: [
              {
                path: "mdm/reasons",
                element: (
                  <LazyPage>
                    <ReasonsListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(PermissionCodes.MdmLossTypeList, PermissionCodes.MdmLossTypeManage),
            children: [
              {
                path: "mdm/loss-types",
                element: (
                  <LazyPage>
                    <LossTypesListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: guard(
              PermissionCodes.MdmPartnerSegmentList,
              PermissionCodes.MdmPartnerSegmentManage,
            ),
            children: [
              {
                path: "mdm/partner-segments",
                element: (
                  <LazyPage>
                    <PartnerSegmentsListPage />
                  </LazyPage>
                ),
              },
            ],
          },
];
